import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useMemo, useState } from "react";
import PetInformation from "../PetInformation";

type SerumFormData = {
  reportDate: string;
  microchipNo: string;
  rabiesVaccinationDate: string;
  bloodSamplingDate: string;
  doctorName: string;
  doctorRegistrationNumber: string;
};

const RabiesTitreSerumCollection = () => {
  const { getRouteKey } = useRouter();
  const { formatDate } = useUtils();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { customerDetails, petDetails } = routeState.state as ICustomerProfileState;

  const initialData: SerumFormData = useMemo(
    () => ({
      reportDate: "",
      microchipNo: "",
      rabiesVaccinationDate: "",
      bloodSamplingDate: "",
      doctorName: "",
      doctorRegistrationNumber: "",
    }),
    [],
  );

  const [formData, setFormData] = useState<SerumFormData>(initialData);

  useEffect(() => {
    if (!customerDetails || !petDetails) return;

    setFormData((prev) => ({
      ...prev,
      reportDate: prev.reportDate || formatDate(),
      microchipNo: prev.microchipNo || petDetails?.microchip_number || "",
    }));
  }, [customerDetails, petDetails]);

  const onValueChange = (field: keyof SerumFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldRow = (
    label: string,
    field: keyof SerumFormData,
    type: "text" | "date" = "text",
    inputClassName?: string,
  ) => (
    <div className="flex items-start gap-3 py-1.5">
      <p className="w-[240px] text-xs font-semibold">{label}</p>
      <input
        type={type}
        className={`w-full border border-gray-300 p-1 text-xs focus:outline-none print:border-0 ${inputClassName ?? ""}`}
        value={formData[field]}
        onChange={(e) => onValueChange(field, e.target.value)}
      />
    </div>
  );

  return (
    <div className="text-xs leading-5">
      <p className="text-right pb-3">
        Date:{" "}
        <input
          type="text"
          className="border border-gray-300 p-1 focus:outline-none print:border-0"
          value={formData.reportDate}
          onChange={(e) => onValueChange("reportDate", e.target.value)}
        />
      </p>

      <p className="text-center font-semibold underline pb-4">TO WHOMSOEVER IT MAY CONCERN</p>

      <PetInformation source="HELTH" />
      {fieldRow("Microchip No:", "microchipNo", "text", "!w-[220px]")}
      {fieldRow("Date of Rabies vaccination:", "rabiesVaccinationDate", "date", "!w-[180px]")}
      {fieldRow("Date of Blood Sampling:", "bloodSamplingDate", "date", "!w-[180px]")}

      <p className="pt-6">
        The above-mentioned dog/cat&apos;s blood is being drawn for Rabies Serological Test. It is being sent for
        medical purpose. This sample is non infectious, non hazardous and has no commercial value.
      </p>

      <div className="pt-12">
        {fieldRow("Doctor Name:", "doctorName", "text", "!w-[220px]")}
        {fieldRow("Doctor Registration Number:", "doctorRegistrationNumber", "text", "!w-[220px]")}
      </div>

      <style>
        {`
          @media print {
            input {
              border: none !important;
            }
            input[type="date"] {
              appearance: none;
              -webkit-appearance: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default RabiesTitreSerumCollection;
